import { useTheme } from "@/contexts/ThemeContext";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { FaGlobe, FaMoon, FaSun } from "react-icons/fa";

export const FloatingActionButtons: React.FC = () => {
  const { i18n } = useTranslation();

  // Safe theme hook with fallback
  let theme: "light" | "dark" = "light";
  let toggleTheme = () => {};

  try {
    const themeContext = useTheme();
    theme = themeContext.theme;
    toggleTheme = themeContext.toggleTheme;
  } catch (error) {
    console.warn(
      "FloatingActionButtons: ThemeContext not available, using defaults"
    );
  }

  const [showLangMenu, setShowLangMenu] = useState(false);

  const changeLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
    setShowLangMenu(false);
  };

  const currentLang = i18n.language || "en";

  return (
    <div className="fixed bottom-6 right-6 z-[9999] flex flex-col gap-3">
      {/* Language Switcher with Menu */}
      <div className="relative">
        {showLangMenu && (
          <div className="absolute bottom-full right-0 mb-2 bg-[var(--bg-card)] border border-[var(--border-light)] rounded-xl shadow-xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <button
              onClick={() => changeLanguage("en")}
              className={`w-full px-4 py-3 text-left hover:bg-[var(--bg-hover)] transition-colors flex items-center gap-3 ${
                currentLang === "en"
                  ? "bg-primary/10 text-primary font-semibold"
                  : "text-[var(--text-primary)]"
              }`}
            >
              <span className="text-lg">🇬🇧</span>
              <span className="text-sm">English</span>
            </button>
            <button
              onClick={() => changeLanguage("hi")}
              className={`w-full px-4 py-3 text-left hover:bg-[var(--bg-hover)] transition-colors flex items-center gap-3 ${
                currentLang === "hi"
                  ? "bg-primary/10 text-primary font-semibold"
                  : "text-[var(--text-primary)]"
              }`}
            >
              <span className="text-lg">🇮🇳</span>
              <span className="text-sm">हिंदी</span>
            </button>
          </div>
        )}
        <button
          onClick={() => setShowLangMenu(!showLangMenu)}
          className="w-14 h-14 rounded-full bg-gradient-to-br from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center group hover:scale-110 active:scale-95"
          aria-label="Change Language"
          title="Change Language"
        >
          <FaGlobe className="text-xl group-hover:rotate-12 transition-transform duration-300" />
        </button>
      </div>

      {/* Theme Switcher */}
      <button
        onClick={toggleTheme}
        className="w-14 h-14 rounded-full bg-gradient-to-br from-primary to-pink-600 hover:from-pink-600 hover:to-primary text-white shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center group hover:scale-110 active:scale-95"
        aria-label={
          theme === "dark" ? "Switch to Light Mode" : "Switch to Dark Mode"
        }
        title={
          theme === "dark" ? "Switch to Light Mode" : "Switch to Dark Mode"
        }
      >
        {theme === "dark" ? (
          <FaSun className="text-xl group-hover:rotate-90 transition-transform duration-300" />
        ) : (
          <FaMoon className="text-xl group-hover:-rotate-12 transition-transform duration-300" />
        )}
      </button>
    </div>
  );
};
